/**
 * Weather File Selector Component
 *
 * Dropdown for selecting bundled weather files, uploading custom ones,
 * or browsing the global weather file library.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useFileStore, WEATHER_FILES, isWeatherFile } from '../index';
import type { WeatherFileInfo } from '../types';
import { WeatherFileLibrary } from '../../weather';
import type { WeatherFileMetadata } from '../../weather';
import { useIsMobile } from '../../hooks';

interface WeatherFileSelectorProps {
  onWeatherFileChange?: (weatherFile: WeatherFileInfo | null) => void;
  onCustomFileSelect?: (file: File) => void;
}

export function WeatherFileSelector({ onWeatherFileChange, onCustomFileSelect }: WeatherFileSelectorProps) {
  const isMobile = useIsMobile();
  const { selectedWeatherFileId, setSelectedWeatherFile } = useFileStore();
  const [customFile, setCustomFile] = useState<File | null>(null);
  const [libraryFile, setLibraryFile] = useState<WeatherFileMetadata | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // Update dropdown position when opened
  useEffect(() => {
    if (dropdownOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [dropdownOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!dropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const selectedWeather =
    selectedWeatherFileId && !customFile && !libraryFile
      ? WEATHER_FILES.find((w) => w.id === selectedWeatherFileId)
      : null;

  const handleSelectWeather = useCallback(
    (weather: WeatherFileInfo) => {
      setSelectedWeatherFile(weather.id);
      setCustomFile(null);
      setLibraryFile(null);
      setDropdownOpen(false);
      onWeatherFileChange?.(weather);
    },
    [setSelectedWeatherFile, onWeatherFileChange]
  );

  const handleSelectFromLibrary = useCallback(
    (file: WeatherFileMetadata) => {
      // Convert WeatherFileMetadata to WeatherFileInfo format
      const weatherInfo: WeatherFileInfo = {
        id: file.id,
        name: file.name,
        url: file.urls.epw,
        location: `${file.location.city}, ${file.location.region}, ${file.location.country}`,
        latitude: file.location.latitude,
        longitude: file.location.longitude,
        elevation: file.location.elevation,
        timezone: file.location.timezone,
        source: file.source.type,
      };

      setLibraryFile(file);
      setCustomFile(null);
      setSelectedWeatherFile(null);
      setLibraryOpen(false);
      setDropdownOpen(false);
      onWeatherFileChange?.(weatherInfo);
    },
    [setSelectedWeatherFile, onWeatherFileChange]
  );

  const handleCustomFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (file && isWeatherFile(file.name)) {
          setCustomFile(file);
          setLibraryFile(null);
          setSelectedWeatherFile(null);
          setDropdownOpen(false);
          onWeatherFileChange?.(null);
          onCustomFileSelect?.(file);
        }
      }
    },
    [setSelectedWeatherFile, onWeatherFileChange, onCustomFileSelect]
  );

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleOpenLibrary = useCallback(() => {
    setDropdownOpen(false);
    setLibraryOpen(true);
  }, []);

  const handleClear = useCallback(() => {
    setCustomFile(null);
    setLibraryFile(null);
    setSelectedWeatherFile(null);
    onWeatherFileChange?.(null);
  }, [setSelectedWeatherFile, onWeatherFileChange]);

  const displayName = customFile
    ? customFile.name
    : libraryFile
    ? libraryFile.name
    : selectedWeather
    ? selectedWeather.name
    : 'Select weather file';

  const displayLocation = libraryFile
    ? `${libraryFile.location.city}, ${libraryFile.location.region}, ${libraryFile.location.country}`
    : selectedWeather
    ? selectedWeather.location
    : null;

  return (
    <div className="relative">
      <label className="mb-1 block text-sm font-medium">Weather File</label>

      {/* Selection button */}
      <button
        ref={buttonRef}
        onClick={() => { setDropdownOpen(!dropdownOpen); }}
        className="flex w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm hover:bg-muted/50"
      >
        <span className={customFile || libraryFile || selectedWeather ? '' : 'text-muted-foreground'}>
          {displayName}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`text-muted-foreground transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* Dropdown - rendered in portal */}
      {dropdownOpen && createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-[9999] rounded-md border bg-popover shadow-lg"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
          }}
        >
          <div className="max-h-64 overflow-y-auto p-1">
            {/* Browse library option */}
            <button
              onClick={handleOpenLibrary}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-primary"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
                <path d="M2 12h20" />
              </svg>
              <span className="font-medium text-primary">Browse Global Library...</span>
            </button>

            <div className="my-1 h-px bg-border" />

            {/* Upload option */}
            <button
              onClick={handleUploadClick}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-muted-foreground"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              Upload custom EPW file...
            </button>

            <div className="my-1 h-px bg-border" />

            {/* Quick picks section */}
            <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
              Quick Picks
            </div>

            {/* Bundled weather files */}
            {WEATHER_FILES.map((weather) => (
              <button
                key={weather.id}
                onClick={() => { handleSelectWeather(weather); }}
                className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground ${
                  selectedWeatherFileId === weather.id
                    ? 'bg-accent text-accent-foreground'
                    : ''
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-muted-foreground"
                >
                  <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
                </svg>
                <div className="flex-1 text-left">
                  <div>{weather.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {weather.location}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}

      {/* Weather info for bundled or library files */}
      {(selectedWeather || libraryFile) && (
        <div className="mt-2 rounded bg-muted/50 p-2 text-xs text-muted-foreground">
          <div className="flex items-center justify-between">
            <span>Location: {displayLocation}</span>
            <button
              onClick={handleClear}
              className="text-red-500 hover:text-red-600"
            >
              Clear
            </button>
          </div>
          <div className="mt-1 flex gap-4">
            <span>
              Lat: {(libraryFile?.location.latitude ?? selectedWeather?.latitude ?? 0).toFixed(2)}
            </span>
            <span>
              Lon: {(libraryFile?.location.longitude ?? selectedWeather?.longitude ?? 0).toFixed(2)}
            </span>
            <span>
              Elev: {libraryFile?.location.elevation ?? selectedWeather?.elevation ?? 0}m
            </span>
          </div>
          {libraryFile && (
            <div className="mt-1 flex items-center gap-2">
              {libraryFile.climateZone.ashrae && (
                <span className="rounded bg-blue-100 px-1.5 py-0.5 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                  Zone {libraryFile.climateZone.ashrae}
                </span>
              )}
              <span className="rounded bg-gray-100 px-1.5 py-0.5 dark:bg-gray-800">
                {libraryFile.source.type}
              </span>
            </div>
          )}
        </div>
      )}

      {customFile && (
        <div className="mt-2 flex items-center justify-between rounded bg-muted/50 p-2 text-xs">
          <span className="text-muted-foreground">
            Custom file: {customFile.name}
          </span>
          <button
            onClick={handleClear}
            className="text-red-500 hover:text-red-600"
          >
            Clear
          </button>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".epw"
        className="hidden"
        onChange={handleCustomFile}
      />

      {/* Weather File Library Dialog */}
      {libraryOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className={`overflow-hidden bg-background shadow-xl ${
            isMobile
              ? 'h-full w-full pb-14'
              : 'h-[80vh] w-[90vw] max-w-5xl rounded-lg'
          }`}
          style={isMobile ? { paddingBottom: 'calc(3.5rem + env(safe-area-inset-bottom))' } : undefined}
          >
            <WeatherFileLibrary
              onSelect={handleSelectFromLibrary}
              onClose={() => setLibraryOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
